import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ShieldAlert, Sparkles, Trash2, GitMerge, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";

// Lista oficial pre-poblada (Phase 5.8d aprobada por el usuario).
const DEFAULT_LIST = [
  "CRISTIAN SAMANIEGO","RAUDEL POSADAS","CARLOS AVILA","EFRAIN GALVAN","CARLOS SEGURA",
  "BENEDICTO DURAN","NAYDELIN MORIEL","CRISTIAN HERNANDEZ","CARIDAD CARRANZA","JOSE TAPIA",
  "PAULINHO PENA","JESUS ZARATE","YASSER BERRIOS","DIANA ARELLANO","ALEXANDER LLAMAS",
  "GALILEA CANALES","CRISTIAN GARCIA","CAMERON SOLOMON","FRANCISCO PEYNADO","DAVID HERNANDEZ",
  "ERICK VASQUEZ","EDUARDO ARREOLA","JOSUE HERNANDEZ","ALEJANDRO GUERRA","JOSE EFRAIN LOPEZ",
  "LAZARO ELIZANDE","EDWIN CRUZ","CELSO BAILON","MONICA UVALLE","LUIS MARTINEZ",
  "IRIS SANCHEZ","ROBERTO NIETO","ROBERTO TAPIA","CRISTIAN CANALES","FIDEL POSADAS",
  "RICHARD RAMIREZ","ALEX POSADAS","VICTOR HERNANDEZ","LUIS ALBERTO COTTO","FRANKLIN GARCIA",
].join("\n");

const DEFAULT_MERGES = [
  { primary: "DAVID HERNANDEZ",    aliases: "DAVIID HERNANDEZ" },
  { primary: "CRISTIAN HERNANDEZ", aliases: "Cris Hernandez" },
  { primary: "CRISTIAN GARCIA",    aliases: "Christian Garcia" },
  { primary: "JOSE EFRAIN LOPEZ",  aliases: "Jose Lopez Efrain" },
  { primary: "LAZARO ELIZANDE",    aliases: "Lazaro Elizalde" },
  { primary: "LUIS ALBERTO COTTO", aliases: "Luis Cotto" },
  { primary: "JOSE TAPIA",         aliases: "Jose A Tapia" },
];

export default function EmployeesCleanupDialog({ open, onOpenChange, onApplied }) {
  const [listText, setListText] = useState(DEFAULT_LIST);
  const [merges, setMerges] = useState(DEFAULT_MERGES);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // reset() DEBE limpiar también los flags de loading para evitar que el modal
  // se quede "Generando…" al re-abrirlo tras haber sido cerrado en pleno vuelo
  // (bug reproducido: cerrar el diálogo antes de que la request termine dejaba
  // loadingPreview=true en memoria y el botón atascado indefinidamente sin toast).
  const reset = () => {
    setListText(DEFAULT_LIST);
    setMerges(DEFAULT_MERGES);
    setPreview(null);
    setConfirming(false);
    setLoadingPreview(false);
    setLoadingExecute(false);
  };

  const handleGenerate = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const canonical_list = listText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const mergesPayload = merges
        .filter((m) => m.primary.trim() && m.aliases.trim())
        .map((m) => ({
          primary: m.primary.trim(),
          aliases: m.aliases
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean),
        }));
      const res = await api.post("/employees-canonical/cleanup/preview", {
        canonical_list,
        merges: mergesPayload,
      });
      setPreview(res.data);
    } catch (e) {
      // Log a consola para depurar en producción si vuelve a atascarse.
      console.error("[cleanup/preview] failed:", e);
      const msg = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error(`Error generando preview: ${msg}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    if (!preview?.preview_id) return;
    setLoadingExecute(true);
    try {
      const res = await api.post("/employees-canonical/cleanup/execute", {
        preview_id: preview.preview_id,
      });
      toast.success(
        `Limpieza completa: ${res.data.deleted} eliminados, ${res.data.merged} fusionados, ${res.data.created} creados, ${res.data.reattributed_count} datos re-atribuidos a DESCONOCIDO`,
        { duration: 8000 },
      );
      reset();
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      console.error("[cleanup/execute] failed:", e);
      const msg = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error(`Error al ejecutar: ${msg}`);
    } finally {
      setLoadingExecute(false);
    }
  };

  const updateMerge = (idx, key, val) => {
    setMerges((curr) => curr.map((m, i) => (i === idx ? { ...m, [key]: val } : m)));
  };
  const addMerge = () =>
    setMerges((curr) => [...curr, { primary: "", aliases: "" }]);
  const removeMerge = (idx) =>
    setMerges((curr) => curr.filter((_, i) => i !== idx));

  const totals = preview?.totals || {};
  const matches = preview?.match_summary || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="cleanup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500" />
            Limpieza de empleados canónicos
          </DialogTitle>
          <DialogDescription>
            Define la lista oficial y las fusiones. El preview es <strong>read-only</strong> y expira en 10 min.
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        {!preview && (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium" htmlFor="cleanup-list">
                Lista oficial (un nombre por línea)
              </label>
              <Textarea
                id="cleanup-list"
                value={listText}
                onChange={(e) => setListText(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                data-testid="cleanup-list-textarea"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {listText.split("\n").filter((s) => s.trim()).length} nombres en la lista
              </p>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium">Fusiones (primary ← alias separados por coma)</span>
                <Button size="sm" variant="ghost" onClick={addMerge} data-testid="cleanup-add-merge">
                  <Plus className="mr-1 h-3 w-3" /> Añadir
                </Button>
              </div>
              <div className="space-y-1.5">
                {merges.map((m, i) => (
                  <div key={i} className="flex gap-1.5" data-testid={`cleanup-merge-row-${i}`}>
                    <input
                      type="text"
                      placeholder="Primary"
                      value={m.primary}
                      onChange={(e) => updateMerge(i, "primary", e.target.value)}
                      className="h-8 w-1/3 rounded-md border border-border bg-background px-2 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Aliases (coma-separados)"
                      value={m.aliases}
                      onChange={(e) => updateMerge(i, "aliases", e.target.value)}
                      className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs"
                    />
                    <Button size="icon" variant="ghost" onClick={() => removeMerge(i)} className="h-8 w-8">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={loadingPreview}
                data-testid="cleanup-generate-preview"
              >
                {loadingPreview ? (
                  <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Generando…</>
                ) : (
                  <><Sparkles className="mr-2 h-3.5 w-3.5" />Generar preview</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-5 gap-2">
              <Stat label="Mantener" value={totals.will_keep} color="emerald" />
              <Stat label="Crear" value={totals.will_create} color="sky" />
              <Stat label="Borrar" value={totals.will_delete} color="rose" />
              <Stat label="Fusionar" value={totals.will_merge} color="amber" />
              <Stat label="Datos → DESCON." value={totals.data_reattributed_to_desconocido} color="zinc" />
            </div>

            <Tabs defaultValue="keep" data-testid="cleanup-tabs">
              <TabsList className="w-full">
                <TabsTrigger value="keep" data-testid="tab-cleanup-keep">
                  Mantener ({matches.exact_matches?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="create" data-testid="tab-cleanup-create">
                  Crear ({matches.will_create?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="delete" data-testid="tab-cleanup-delete">
                  Borrar ({matches.to_delete?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="merge" data-testid="tab-cleanup-merge">
                  Fusionar ({matches.to_merge?.length || 0})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="keep" className="mt-2 max-h-72 overflow-y-auto rounded border border-border p-2">
                <ul className="space-y-0.5 text-xs">
                  {matches.exact_matches?.map((m) => (
                    <li key={m.canonical_id} className="flex items-center gap-2">
                      <span className="text-muted-foreground">{m.list_name}</span>
                      <span>→</span>
                      <span className="font-medium">{m.db_name}</span>
                    </li>
                  ))}
                </ul>
              </TabsContent>
              <TabsContent value="create" className="mt-2 max-h-72 overflow-y-auto rounded border border-border p-2">
                <ul className="space-y-0.5 text-xs">
                  {matches.will_create?.map((c) => (
                    <li key={c.name_norm}><Plus className="inline h-3 w-3 mr-1 text-sky-400" />{c.name}</li>
                  ))}
                </ul>
              </TabsContent>
              <TabsContent value="delete" className="mt-2 max-h-72 overflow-y-auto rounded border border-border">
                <table className="w-full text-xs" data-testid="cleanup-delete-table">
                  <thead className="bg-muted/40">
                    <tr><th className="px-2 py-1 text-left">Nombre</th><th className="px-2 py-1 text-right">Tx</th><th className="px-2 py-1 text-right">Time entries</th><th className="px-2 py-1 text-right">Schedule</th><th className="px-2 py-1 text-right">Aliases</th></tr>
                  </thead>
                  <tbody>
                    {(matches.to_delete || [])
                      .sort((a, b) => b.transactions - a.transactions)
                      .map((d) => (
                        <tr key={d.canonical_id} className="border-t border-border/40">
                          <td className="px-2 py-1">{d.name}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{d.transactions.toLocaleString()}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{d.time_entries}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{d.schedule_entries}</td>
                          <td className="px-2 py-1 text-right">{d.aliases_count}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </TabsContent>
              <TabsContent value="merge" className="mt-2 max-h-72 overflow-y-auto rounded border border-border p-2">
                {matches.to_merge?.map((m, i) => (
                  <div key={i} className="mb-2 text-xs">
                    <div className="font-medium flex items-center gap-1">
                      <GitMerge className="h-3 w-3 text-amber-400" />
                      {m.primary.name} {m.primary.exists ? <Badge variant="outline" className="text-[9px]">existe</Badge> : <Badge variant="outline" className="text-[9px] border-sky-500/40">se creará</Badge>}
                    </div>
                    {m.secondaries.map((s, j) => (
                      <div key={j} className="ml-4 mt-0.5 text-muted-foreground">
                        ← {s.name} (tx={s.transactions}, te={s.time_entries})
                      </div>
                    ))}
                  </div>
                ))}
              </TabsContent>
            </Tabs>

            <DialogFooter className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <Button variant="ghost" onClick={() => setPreview(null)} data-testid="cleanup-back">
                ← Volver
              </Button>
              {!confirming ? (
                <Button
                  variant="destructive"
                  onClick={() => setConfirming(true)}
                  data-testid="cleanup-show-confirm"
                >
                  <ShieldAlert className="mr-2 h-3.5 w-3.5" />
                  Confirmar limpieza
                </Button>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/5 p-2 text-xs">
                  <ShieldAlert className="h-4 w-4 text-red-500" />
                  <span>¿Estás seguro? Esta acción es <strong>irreversible</strong>.</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleExecute}
                    disabled={loadingExecute}
                    data-testid="cleanup-execute-confirm"
                  >
                    {loadingExecute ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sí, ejecutar"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                    Cancelar
                  </Button>
                </div>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    emerald: "border-emerald-500/40 text-emerald-300",
    sky: "border-sky-500/40 text-sky-300",
    rose: "border-rose-500/40 text-rose-300",
    amber: "border-amber-500/40 text-amber-300",
    zinc: "border-zinc-500/40 text-zinc-300",
  };
  return (
    <div className={`rounded-md border bg-card/40 p-2 ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
